import React, { useEffect, useState } from "react";
import { User, Building2 } from "lucide-react";
import { toast } from "sonner";
import * as api from "../../lib/api";
import { useApp } from "../../context/AppContext";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { UserSearchSelect } from "../ui/UserSearchSelect";
import { HandoverSection, emptyHandover } from "./HandoverSection";

// Attribution d'un item : à une personne, ou (optionnel) à un département — matériel partagé —
// avec un responsable et un utilisateur prévu facultatifs.
export function AssignDialog({ item, open, onClose, onDone, people, departments, testPrefix = "assign" }) {
    const { currentUser } = useApp();
    const [mode, setMode] = useState("person");
    const [target, setTarget] = useState(null);
    const [departmentId, setDepartmentId] = useState("");
    const [responsible, setResponsible] = useState(null);
    const [intended, setIntended] = useState(null);
    const [handover, setHandover] = useState(emptyHandover(currentUser?.id));
    const [photos, setPhotos] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setMode("person"); setTarget(null); setDepartmentId(""); setResponsible(null); setIntended(null);
        setHandover(emptyHandover(currentUser?.id)); setPhotos([]);
    }, [open, currentUser?.id]);

    if (!item) return null;
    const canSubmit = mode === "person" ? !!target : !!departmentId;
    const sortedDepts = [...(departments || [])].sort((a, b) => (a.name || "").localeCompare(b.name || ""));

    const submit = async () => {
        if (!canSubmit) return;
        setSaving(true);
        try {
            const base = {
                condition: handover.condition,
                condition_notes: handover.condition_notes,
                handover_date: handover.date_undefined ? null : handover.date,
                handover_date_undefined: !!handover.date_undefined,
                handed_over_by: handover.handed_over_by,
            };
            const payload = mode === "person"
                ? { ...base, user_id: target }
                : { ...base, department_id: departmentId, responsible_user_id: responsible || null, intended_user_id: intended || null };
            const res = await api.assignInventoryItem(item.id, payload);
            for (const f of photos) {
                await api.uploadInventoryAttachment(item.id, f, `Photo remise ${handover.date || ""}`.trim(), { category: "etat_des_lieux", historyId: res.history_id });
            }
            const deptName = sortedDepts.find((d) => d.id === departmentId)?.name;
            toast.success(mode === "person" ? `"${item.asset_tag || item.name}" assigné` : `"${item.asset_tag || item.name}" attribué au département ${deptName || ""}`);
            onDone?.(res);
            onClose();
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-lg" data-testid={`${testPrefix}-dialog`}>
                <DialogHeader><DialogTitle>Assigner "{item.asset_tag || item.name}"</DialogTitle></DialogHeader>
                <div className="max-h-[65vh] overflow-y-auto py-1 pr-1 space-y-3">
                    <div className="grid grid-cols-2 gap-2" data-testid={`${testPrefix}-mode-group`}>
                        {[["person", User, "Une personne", "Remise nominative"], ["department", Building2, "Un département", "Matériel partagé (optionnel : responsable + utilisateur)"]].map(([k, Icon, l, d]) => (
                            <button key={k} type="button" onClick={() => setMode(k)} data-testid={`${testPrefix}-mode-${k}`}
                                className={`text-left px-3 py-2 rounded-md border text-sm transition-colors ${mode === k ? "bg-gray-900 text-white border-gray-900" : "bg-white border-gray-300 hover:bg-gray-50"}`}>
                                <span className="font-medium flex items-center gap-1.5"><Icon className="w-3.5 h-3.5" />{l}</span>
                                <span className={`text-[11px] block ${mode === k ? "text-gray-300" : "text-gray-500"}`}>{d}</span>
                            </button>
                        ))}
                    </div>

                    {mode === "person" ? (
                        <div><label className="form-label text-xs">Employé</label>
                            <UserSearchSelect users={people} departments={departments} value={target} onChange={setTarget} placeholder="Sélectionner..." data-testid={`${testPrefix}-person-select`} />
                        </div>
                    ) : (
                        <div className="space-y-3 rounded-md border border-teal-100 bg-teal-50/40 p-3">
                            <div>
                                <label className="form-label text-xs">Département *</label>
                                <select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)} className="w-full px-3 py-2 text-sm border rounded-md bg-white" data-testid={`${testPrefix}-department-select`}>
                                    <option value="">Sélectionner un département…</option>
                                    {sortedDepts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="form-label text-xs">Responsable <span className="text-gray-400 font-normal">(optionnel)</span></label>
                                <UserSearchSelect users={people} departments={departments} value={responsible} onChange={setResponsible} placeholder="Qui en est responsable ?" data-testid={`${testPrefix}-responsible-select`} />
                            </div>
                            <div>
                                <label className="form-label text-xs">Utilisateur prévu <span className="text-gray-400 font-normal">(optionnel)</span></label>
                                <UserSearchSelect users={people} departments={departments} value={intended} onChange={setIntended} placeholder="Qui est censé l'utiliser ?" data-testid={`${testPrefix}-intended-select`} />
                            </div>
                            <p className="text-[10px] text-gray-500">Le matériel reste rattaché au département (ex. salle de réunion, poste partagé). Il apparaîtra sur la fiche du responsable et de l'utilisateur prévu dans « Matériel partagé ».</p>
                        </div>
                    )}

                    <HandoverSection value={handover} onChange={setHandover} photos={photos} setPhotos={setPhotos} people={people} departments={departments} mode="remise" />
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Annuler</Button>
                    <Button onClick={submit} disabled={!canSubmit || saving} data-testid={`${testPrefix}-confirm-btn`}>{saving ? "Enregistrement…" : mode === "person" ? "Assigner" : "Attribuer au département"}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// Affichage compact de l'attribution (personne ou département)
export function AssigneeCell({ item, userMap, deptMap, onOpenEmployee, linkProps }) {
    if (item.assignment_type === "department") {
        const resp = item.responsible_user_id ? userMap[item.responsible_user_id] : null;
        const intended = item.intended_user_id ? userMap[item.intended_user_id] : null;
        return (
            <div className="min-w-0" data-testid={`assignee-dept-${item.id}`}>
                <span className="inline-flex items-center gap-1 text-xs font-medium text-teal-700"><Building2 className="w-3 h-3" />{deptMap?.[item.assigned_department_id] || "Département"}</span>
                {(resp || intended) && (
                    <div className="text-[10px] text-gray-500 leading-tight">
                        {resp && <span>Resp. <button className="text-blue-600 hover:underline" {...(linkProps ? linkProps(`/admin/inventory/employee/${item.responsible_user_id}`, true) : { onClick: (e) => { e.stopPropagation(); onOpenEmployee?.(item.responsible_user_id); } })}>{resp}</button></span>}
                        {resp && intended && <span> · </span>}
                        {intended && <span>Util. <button className="text-blue-600 hover:underline" {...(linkProps ? linkProps(`/admin/inventory/employee/${item.intended_user_id}`, true) : { onClick: (e) => { e.stopPropagation(); onOpenEmployee?.(item.intended_user_id); } })}>{intended}</button></span>}
                    </div>
                )}
            </div>
        );
    }
    return null;
}
